/**
 * StepUpInterceptor — global TanStack Query mutation-cache subscriber.
 *
 * Phase 11, reworked for FF-UI-2: when ANY mutation across the app fails with
 * `STEP_UP_REQUIRED`, enqueue the failed mutation on the step-up queue and let
 * the global StepUpModalHost run the MFA challenge IN PLACE, then replay the
 * mutation with its original variables.
 *
 * The previous behaviour navigated to /auth/refresh-step-up — a full SPA
 * navigation that unmounted whatever modal/wizard the user was in (losing all
 * form state) and never replayed the mutation, so the user's action was
 * silently dropped (FF-UI-2). The /auth/refresh-step-up route still exists for
 * pre-emptive gates (superadmin layout, the dashboard "Verify to reveal" CTA,
 * deep links); this interceptor no longer targets it.
 *
 * Centralising means every step-up-gated mutation (BO writes, lifecycle ops,
 * trustee appoints, team-invite, billing, client onboarding, etc.) gets the
 * modal + replay for free — no per-screen boilerplate.
 *
 * Mount once near the root of the routed tree (inside QueryClientProvider),
 * alongside <StepUpModalHost />. Renders nothing.
 *
 * Why mutation-cache and not query-cache: step-up-gated READS exist (e.g. the
 * super-admin AUM/MRR rollups call requireStepUp server-side), but they are
 * deliberately handled per-component with a "Verify to reveal" affordance —
 * auto-opening a modal from a background query refetch would interrupt the
 * user unprompted. Mutations are always user-initiated, so the modal is
 * always expected.
 *
 * Replay-loop guard: a mutation that was already replayed once and STILL
 * fails STEP_UP_REQUIRED is not re-queued — it falls through to the owning
 * surface's normal error rendering.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from './client';
import { useStepUpQueue, wasReplayed, type AnyMutation } from './step-up-store';

export function StepUpInterceptor() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const cache = queryClient.getMutationCache();
    const unsubscribe = cache.subscribe((event) => {
      if (event.type !== 'updated') return;
      const mutation = event.mutation;
      if (mutation.state.status !== 'error') return;
      const err = mutation.state.error;
      if (!(err instanceof ApiError) || !err.isStepUpRequired) return;
      const m = mutation as AnyMutation;
      if (wasReplayed(m)) return; // second failure post-replay → surface UI
      useStepUpQueue.getState().enqueue(m);
    });
    return () => unsubscribe();
  }, [queryClient]);

  return null;
}
